import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../Modal';
import { Donut, HBarChart, formatBytes, formatNumber, PALETTE } from '../charts';
import { Badge, Skeleton } from '../ui';
import { loadWorkspaces } from '../../observability/client';

/* ── Workspace usage by user: the Agent workspaces dialog ──────────────────────
   The figures are the control-plane's own measurement of each user's folder
   (backend/services/workspace_usage.py), not EFS's per-filesystem `SizeInBytes`: EFS
   cannot say how much of a shard one directory holds. So they lag by up to one
   measuring interval for an active user, and the shard totals in the table behind this
   dialog (metered by EFS) will not match them to the byte. */

const DONUT_SLICES = 8;
const LIST_LIMIT = 100;

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const pct = (v) => `${v >= 10 ? v.toFixed(0) : v.toFixed(1)}%`;

function slices(users, total) {
  const top = users.slice(0, DONUT_SLICES).map((u, i) => ({
    name: u.username, value: u.bytes_used, color: PALETTE[i % PALETTE.length],
  }));
  const rest = total - top.reduce((s, d) => s + d.value, 0);
  if (rest > 0) top.push({ name: 'Everyone else', value: rest, color: 'var(--ink-4)' });
  return top;
}

export default function WorkspaceUsageModal({ open, onClose, fsId, shardLabel }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ data: null, error: null, loading: false });

  const fetchUsage = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await loadWorkspaces({ fsId, limit: LIST_LIMIT });
      setState({ data, error: null, loading: false });
    } catch (e) {
      setState({ data: null, error: e.message, loading: false });
    }
  }, [fsId]);

  useEffect(() => { if (open) fetchUsage(); }, [open, fetchUsage]);

  const d = state.data;
  const users = d?.users || [];
  const listed = users.reduce((s, u) => s + u.bytes_used, 0);
  const title = shardLabel ? `Workspace usage on ${shardLabel}` : 'Workspace usage by user';

  return (
    <Modal open={open} onClose={onClose} title={title} maxWidth={820}
           description="Each user's folder as last measured by the control-plane, largest first.">
      {state.loading && !d && (
        <div role="status" aria-busy="true" className="stack gap-3">
          <Skeleton height={180} />
          <Skeleton height={14} /><Skeleton height={14} /><Skeleton height={14} />
        </div>
      )}
      {state.error && (
        <div role="alert" className="notice notice-danger">
          <div className="notice-body">Could not load workspace usage: {state.error}</div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={fetchUsage}>Retry</button>
        </div>
      )}
      {d && d.measured_users === 0 && (
        <div className="tile-empty">
          {d.unmeasured_users
            ? `No workspace measured yet. ${formatNumber(d.unmeasured_users)} are waiting for the `
              + 'control-plane\'s first pass, which runs every '
              + `${Math.round(d.interval_s / 60)} minutes.`
            : 'No workspace on this shard.'}
        </div>
      )}
      {d && d.measured_users > 0 && (
        <>
          <div className="workspace-usage-summary">
            <Donut data={slices(users, d.total_bytes)} height={200} formatVal={v => formatBytes(v)}
                   centerLabel={formatBytes(d.total_bytes)}
                   centerSub={`${formatNumber(d.measured_users)} workspaces`} />
            <dl className="kv">
              <dt>Stored</dt>
              <dd className="num">{formatBytes(d.total_bytes)}</dd>
              <dt>Files</dt>
              <dd className="num">{formatNumber(d.total_files)}</dd>
              <dt>Largest</dt>
              <dd>
                <span className="fw-500">{users[0].username}</span>
                <span className="text-muted">{` · ${pct(users[0].share_pct)} of all`}</span>
              </dd>
              <dt>Measured</dt>
              <dd className="text-sm">
                {when(d.oldest_measured_at)} to {when(d.newest_measured_at)}
                <span className="cell-sub">
                  Active users are re-measured every {Math.round(d.interval_s / 60)} min,
                  everyone else once a day.
                </span>
              </dd>
              {(d.unmeasured_users > 0 || d.partial_users > 0) && (
                <>
                  <dt>Not counted</dt>
                  <dd className="row-wrap gap-1">
                    {d.unmeasured_users > 0 && (
                      <Badge tone="neutral">{formatNumber(d.unmeasured_users)} not measured yet</Badge>
                    )}
                    {d.partial_users > 0 && (
                      <Badge tone="warn">{formatNumber(d.partial_users)} too large to finish</Badge>
                    )}
                  </dd>
                </>
              )}
            </dl>
          </div>

          <h4 className="overline mt-4 mb-2">
            {d.measured_users > users.length
              ? `Largest ${users.length} of ${formatNumber(d.measured_users)}`
              : `All ${formatNumber(d.measured_users)} workspaces`}
          </h4>
          <div className="workspace-usage-list">
            <HBarChart
              data={users.map(u => ({ ...u, name: u.username, count: u.bytes_used }))}
              nameWidth={170}
              valueWidth={150}
              formatVal={(v, row) => `${formatBytes(v)} · ${pct(row.share_pct)}${row.status === 'partial' ? '+' : ''}`}
              onClick={row => { onClose(); navigate(`/users/${row.user_id}`); }}
            />
          </div>
          {d.measured_users > users.length && (
            <p className="text-muted text-sm mt-2 mb-0">
              {`The other ${formatNumber(d.measured_users - users.length)} hold `}
              {`${formatBytes(d.total_bytes - listed)} together.`}
            </p>
          )}
          {d.partial_users > 0 && (
            <p className="text-muted text-sm mt-2 mb-0">
              A + marks a folder whose count stopped at its time or file budget: the real size
              is larger.
            </p>
          )}
        </>
      )}
    </Modal>
  );
}
